import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Lock, Eye, EyeOff, Check, Loader2, AlertCircle } from 'lucide-react';

const ForcePasswordChange = () => {
  const { mustChangePassword, changePassword, user, logout } = useAuth();
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  if (!mustChangePassword || !user) return null;

  const validate = () => {
    if (!currentPw) return 'Bitte geben Sie das temporäre Passwort ein.';
    if (newPw.length < 6) return 'Das neue Passwort muss mindestens 6 Zeichen lang sein.';
    if (newPw === currentPw) return 'Das neue Passwort muss sich vom temporären unterscheiden.';
    if (newPw !== confirmPw) return 'Die beiden neuen Passwörter stimmen nicht überein.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setError(null);
    setSubmitting(true);
    try {
      await changePassword(currentPw, newPw);
      setSuccess(true);
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Passwortwechsel fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-[10001] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        data-testid="force-password-modal"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
        >
          <div className="p-6 border-b border-border flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Lock className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground font-[Manrope]">
                Bitte neues Passwort setzen
              </h2>
              <p className="text-xs text-muted-foreground">
                Sicherheits-Voraussetzung beim ersten Login
              </p>
            </div>
          </div>

          {success ? (
            <div className="p-8 text-center" data-testid="force-password-success">
              <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
                <Check className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="font-bold text-foreground mb-1">Passwort geändert</h3>
              <p className="text-sm text-muted-foreground">Sie werden gleich zur Übersicht weitergeleitet…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900">
                Aus Sicherheitsgründen müssen Sie Ihr automatisch erzeugtes Passwort beim ersten Login ändern.
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Temporäres Passwort (aus der Willkommens-E-Mail)
                </label>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)}
                  autoComplete="current-password"
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 font-mono tracking-wider"
                  data-testid="force-password-current"
                  placeholder="z. B. 482915"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Neues Passwort
                </label>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  autoComplete="new-password"
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="force-password-new"
                  placeholder="Mindestens 6 Zeichen"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Neues Passwort bestätigen
                </label>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  autoComplete="new-password"
                  className="w-full px-3 py-2.5 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="force-password-confirm"
                  placeholder="Wiederholen"
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={showPw}
                  onChange={(e) => setShowPw(e.target.checked)}
                  className="w-4 h-4 accent-primary"
                />
                {showPw ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                Passwort anzeigen
              </label>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 flex items-start gap-2" data-testid="force-password-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <button
                  type="button"
                  onClick={logout}
                  className="px-4 py-2.5 rounded-full border border-border text-foreground font-medium hover:bg-muted/50 transition-colors text-sm"
                  data-testid="force-password-logout-btn"
                >
                  Abbrechen & Abmelden
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-5 py-2.5 rounded-full bg-primary text-white font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="force-password-submit"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                  Passwort ändern
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ForcePasswordChange;
